import React, { useEffect, useMemo, useState } from "react";
import { FaCheck, FaEdit, FaGift, FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";
import {
  createNewCommissionBonus,
  createNewCommissionPlan,
  extractCreatedPlanId,
  updateNewCommissionBonus,
  updateNewCommissionPlan,
  deleteNewCommissionBonus,
} from "../commissionsApi";
import { formatCurrency, formatPercent } from "./commissionDetailConstants";
import SetupCardSavedBody from "./commissionSetupCardLayout";
import {
  buildNewCommissionBonusPayload,
  buildNewCommissionPlanPayload,
  calculateExpectedBonus,
  calculateExpectedCommission,
  calculateCommissionGstViews,
  COMMISSION_TYPE_OPTIONS,
  readBonusRecord,
  resolveRateForLocation,
} from "./commissionPlanUtils";

const controlClass =
  "h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-800 outline-none transition hover:border-slate-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-500/20 disabled:cursor-not-allowed disabled:bg-slate-50";

function Chip({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

function FieldLabel({ children, hint }) {
  return (
    <div className="mb-1 flex items-center justify-between gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {children}
      </span>
      {hint ? <span className="text-[10px] text-slate-400">{hint}</span> : null}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="mb-0 text-[9px] font-medium uppercase tracking-wide text-slate-400">
        {label}
      </p>
      <p className="mb-0 mt-0.5 truncate text-[12px] font-semibold text-slate-800">
        {value || "—"}
      </p>
    </div>
  );
}

function toApiGstType(value) {
  if (value === "inclusive" || value === "exclusive") return value;
  return "none";
}

function fromApiGstType(value) {
  if (value === "inclusive" || value === "exclusive") return value;
  return "notax";
}

function taxLabelText(value) {
  const v = fromApiGstType(value);
  if (v === "inclusive") return "Inclusive";
  if (v === "exclusive") return "Exclusive";
  return "No Tax";
}

function taxFormulaText(value) {
  const v = fromApiGstType(value);
  if (v === "inclusive") return "GST = Amount × Rate / (100 + Rate)";
  if (v === "exclusive") return "GST = Amount × Rate / 100";
  return "No GST applied";
}

function toNumberSafe(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function bonusTypeLabel(value) {
  const normalized = String(value || "").toLowerCase();
  return (
    COMMISSION_TYPE_OPTIONS.find((option) => option.value === normalized)
      ?.label || "Fixed amount"
  );
}

function buildBonusTaxRows({
  gstType,
  currency,
  baseExGst,
  exclusiveTotal,
  inclusiveTotal,
  fallbackAmount = 0,
}) {
  const tax = fromApiGstType(gstType);
  if (tax === "notax") {
    return [
      {
        label: "Expected bonus",
        value: formatCurrency(fallbackAmount || baseExGst, currency),
      },
    ];
  }

  return [
    {
      label: "Expected bonus",
      value: formatCurrency(baseExGst, currency),
    },
    {
      label: "Expected bonus with GST",
      value: formatCurrency(
        tax === "inclusive" ? inclusiveTotal : exclusiveTotal,
        currency,
      ),
    },
  ];
}

function resolveBonusGstType(bonus) {
  const raw = String(bonus?.gstType ?? bonus?.gst_type ?? "none").toLowerCase();
  if (raw === "inclusive" || raw === "exclusive") return raw;
  return "none";
}

function commissionableTreatmentLabel(deductFromCommissionable) {
  return deductFromCommissionable
    ? "Deduct bonus from commissionable"
    : "Bonus is separate";
}

function BonusSavedView({
  bonus,
  currency,
  commissionableTuitionFee = 0,
  deductFromCommissionable = false,
}) {
  const record = readBonusRecord(bonus) || bonus;
  const bonusGstType = resolveBonusGstType(record);
  const gstViews = calculateCommissionGstViews({
    tuitionFee: commissionableTuitionFee,
    commissionableTuitionFee,
    commissionType: record.bonusType === "percentage" ? "percentage" : "fixed",
    commissionRate: record.bonusValue,
    commissionGstType: bonusGstType,
  });

  const amountLabel =
    record.bonusType === "percentage"
      ? formatPercent(record.bonusValue, 2)
      : formatCurrency(record.bonusValue, currency);

  const primaryMeta = [
    bonusTypeLabel(record.bonusType),
    taxLabelText(bonusGstType),
    currency,
    commissionableTreatmentLabel(deductFromCommissionable),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex flex-col gap-2.5">
      <div className="grid grid-cols-2 gap-2 rounded-lg border border-amber-100 bg-[#FFFBF5] px-3 py-2.5">
        <Metric label="Bonus name" value={record.bonusName || "—"} />
        <Metric label="Bonus amount" value={amountLabel} />
        <Metric
          label="Commissionable fee"
          value={commissionableTreatmentLabel(deductFromCommissionable)}
        />
        <Metric label="Expected bonus" value={formatCurrency(record.expectedBonus, currency)} />
      </div>
      <SetupCardSavedBody
        heroLabel="Expected bonus"
        primaryValue={formatCurrency(record.expectedBonus, currency)}
        primaryMeta={primaryMeta}
        taxRows={buildBonusTaxRows({
          gstType: bonusGstType,
          currency,
          baseExGst: gstViews.baseExGst,
          exclusiveTotal: gstViews.exclusiveTotal,
          inclusiveTotal: gstViews.inclusiveTotal,
          fallbackAmount: record.expectedBonus,
        })}
        note={record.remarks || ""}
        tone="amber"
      />
    </div>
  );
}

function BonusYesNoToggle({ value, disabled, onChange }) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-amber-200 bg-white p-0.5">
      {[
        { id: false, label: "No" },
        { id: true, label: "Yes" },
      ].map((option) => {
        const active = Boolean(value) === option.id;
        return (
          <button
            key={option.label}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.id)}
            className={`h-7 min-w-[52px] rounded-md px-3 text-[11px] font-semibold transition ${
              active
                ? "bg-amber-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-amber-50"
            } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
            style={{ border: "none" }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function CommissionDetailBonusCard({
  planContext,
  params = {},
  student,
  provider,
  course,
  application,
  currency: currencyProp = "AUD",
  onPlanCreated,
  onSaved,
}) {
  const defaults = planContext?.defaults || {};
  const savedPlan = planContext?.existing || null;
  const bonuses = Array.isArray(planContext?.bonuses)
    ? planContext.bonuses
    : [];
  const existingBonus = bonuses[0] || null;
  const planLoading = Boolean(planContext?.loading);
  const [localBonusEnabled, setLocalBonusEnabled] = useState(false);
  const planBonusEnabled = Boolean(
    savedPlan?.hasBonus ?? existingBonus?.id,
  );
  const hasBonusEnabled = savedPlan?.id
    ? planBonusEnabled
    : localBonusEnabled || Boolean(existingBonus?.id);
  const bonusFromCommissionable = Boolean(
    savedPlan?.bonusFromCommissionable ??
      savedPlan?.bonus_from_commissionable,
  );
  const [deductFromCommissionable, setDeductFromCommissionable] = useState(
    bonusFromCommissionable,
  );
  const location = savedPlan?.location || defaults.location || "offshore";
  // Percentage bonus is based on commissionable tuition (provider commission base)
  const tuitionFee = toNumberSafe(
    defaults.commissionableTuitionFee ??
      savedPlan?.commissionableTuitionFee ??
      defaults.tuitionFee ??
      savedPlan?.tuitionFee,
  );
  const currency =
    savedPlan?.currency || defaults.currency || currencyProp || "AUD";
  const suggestedBonus = resolveRateForLocation({
    location,
    onshoreRate: defaults.onshoreBonus,
    offshoreRate: defaults.offshoreBonus,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [bonusName, setBonusName] = useState("Provider bonus");
  const [bonusType, setBonusType] = useState("fixed");
  const [bonusValue, setBonusValue] = useState("");
  const [taxType, setTaxType] = useState("exclusive");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [togglingBonus, setTogglingBonus] = useState(false);

  const resetFormFromExisting = (bonus) => {
    const record = readBonusRecord(bonus);
    if (record) {
      setBonusName(record.bonusName || "Provider bonus");
      setBonusType(record.bonusType || "fixed");
      const value = record.bonusValue;
      setBonusValue(
        value != null && Number.isFinite(Number(value)) ? String(value) : "",
      );
      setTaxType(fromApiGstType(resolveBonusGstType(record)));
      setRemarks(record.remarks || "");
      return;
    }
    setBonusName("Provider bonus");
    setBonusType(suggestedBonus > 0 ? "percentage" : "fixed");
    setBonusValue(suggestedBonus > 0 ? String(suggestedBonus) : "");
    setTaxType("exclusive");
    setRemarks("");
  };

  useEffect(() => {
    resetFormFromExisting(existingBonus);
  }, [
    existingBonus?.id,
    existingBonus?.bonusValue,
    existingBonus?.bonus_value,
    existingBonus?.updatedAt,
    existingBonus?.updated_at,
    savedPlan?.id,
  ]);

  useEffect(() => {
    setIsEditing(false);
  }, [existingBonus?.id, savedPlan?.id]);

  useEffect(() => {
    setDeductFromCommissionable(bonusFromCommissionable);
  }, [bonusFromCommissionable, savedPlan?.id]);

  const planOptionsDirty =
    Boolean(savedPlan?.id) && deductFromCommissionable !== bonusFromCommissionable;

  const gstType = toApiGstType(taxType);
  const preview = useMemo(
    () =>
      calculateExpectedBonus({
        tuitionFee,
        commissionableTuitionFee: tuitionFee,
        bonusType,
        bonusValue,
        gstType,
      }),
    [tuitionFee, bonusType, bonusValue, gstType],
  );

  const canSubmit =
    !saving &&
    defaults.applicationCourseId &&
    (params.appId || application?.id) &&
    String(bonusName || "").trim() &&
    Number(bonusValue) >= 0;

  const ensureCommissionPlanId = async ({ hasBonus = true } = {}) => {
    if (savedPlan?.id) return savedPlan.id;
    if (!defaults.applicationCourseId) {
      throw new Error(
        "Application course id is required before saving bonus.",
      );
    }

    const planLocation = location || defaults.location || "offshore";
    const planTuitionFee = toNumberSafe(
      defaults.tuitionFee ?? course?.tuitionFee ?? tuitionFee,
    );
    const planCommissionableTuitionFee = toNumberSafe(
      defaults.commissionableTuitionFee ??
        course?.commissionableTuitionFee ??
        tuitionFee,
    );
    const planCommissionRate =
      resolveRateForLocation({
        location: planLocation,
        onshoreRate: defaults.onshoreRate,
        offshoreRate: defaults.offshoreRate,
      }) ||
      toNumberSafe(defaults.commissionRate) ||
      0;
    const planCommissionGstType = "none";
    const commissionPreview = calculateExpectedCommission({
      tuitionFee: planTuitionFee,
      commissionableTuitionFee: planCommissionableTuitionFee,
      commissionType: "percentage",
      commissionRate: planCommissionRate,
      commissionGstType: planCommissionGstType,
    });
    const commissionGstViews = calculateCommissionGstViews({
      tuitionFee: planTuitionFee,
      commissionableTuitionFee: planCommissionableTuitionFee,
      commissionType: "percentage",
      commissionRate: planCommissionRate,
      commissionGstType: planCommissionGstType,
    });

    const payload = buildNewCommissionPlanPayload({
      applicationId: params.appId || application?.id,
      applicationCourseId: defaults.applicationCourseId,
      clientId: params.studentId || student?.id,
      providerId: params.providerId || provider?.id,
      courseId: params.courseId || course?.id,
      levelId: defaults.levelId || course?.levelId,
      location: planLocation,
      tuitionFee: planTuitionFee,
      commissionable_tuition_fee: planCommissionableTuitionFee,
      commissionType: "percentage",
      commissionRate: planCommissionRate,
      commissionGstType: planCommissionGstType,
      expectedCommission: commissionPreview.expectedCommission,
      expectedCommissionExGst: commissionGstViews.baseExGst,
      expectedCommissionExclusive: commissionGstViews.exclusiveTotal,
      expectedCommissionInclusive: commissionGstViews.inclusiveTotal,
      currency,
      status: "active",
      hasBonus,
      bonusFromCommissionable: Boolean(deductFromCommissionable),
    });

    const createResponse = await createNewCommissionPlan(payload);
    const planId = extractCreatedPlanId(createResponse);
    if (!planId) {
      throw new Error("Failed to create commission plan for bonus.");
    }
    if (typeof onPlanCreated === "function") {
      await onPlanCreated(planId);
    }
    return planId;
  };

  const handleBonusToggle = async (nextValue) => {
    if (togglingBonus || Boolean(nextValue) === hasBonusEnabled) {
      return;
    }

    if (!savedPlan?.id) {
      setLocalBonusEnabled(Boolean(nextValue));
      if (nextValue) {
        resetFormFromExisting(existingBonus);
        setIsEditing(true);
      } else {
        setIsEditing(false);
      }
      return;
    }

    if (!nextValue && existingBonus?.id) {
      const confirm = await Swal.fire({
        icon: "question",
        title: "Turn off bonus?",
        text: "The saved bonus will stay on this plan but bonus setup and installments will be hidden until you turn bonus back on.",
        showCancelButton: true,
        confirmButtonText: "Turn off",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#d97706",
        reverseButtons: true,
      });
      if (!confirm.isConfirmed) return;
    }

    setTogglingBonus(true);
    try {
      await updateNewCommissionPlan(savedPlan.id, { has_bonus: Boolean(nextValue) });
      if (!nextValue) {
        setIsEditing(false);
      }
      if (typeof onSaved === "function") await onSaved();
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to update bonus setting.",
        "error",
      );
    } finally {
      setTogglingBonus(false);
    }
  };

  const persistPlanCommissionableOption = async () => {
    if (!savedPlan?.id) return;
    await updateNewCommissionPlan(savedPlan.id, {
      bonus_from_commissionable: Boolean(deductFromCommissionable),
    });
  };

  const handleSave = async () => {
    if (!canSubmit) return;

    setSaving(true);
    try {
      let planId = savedPlan?.id || null;
      if (!planId) {
        planId = await ensureCommissionPlanId({ hasBonus: true });
      } else if (planOptionsDirty) {
        await persistPlanCommissionableOption();
      } else if (!planBonusEnabled) {
        await updateNewCommissionPlan(planId, { has_bonus: true });
      }

      const payload = buildNewCommissionBonusPayload({
        bonusName: String(bonusName).trim(),
        bonusType,
        bonusValue,
        gstType,
        expectedBonus: preview.expectedBonus,
        status: "active",
        remarks,
      });

      if (existingBonus?.id && isEditing) {
        await updateNewCommissionBonus(existingBonus.id, payload);
      } else {
        await createNewCommissionBonus(planId, payload);
      }

      setLocalBonusEnabled(false);
      setIsEditing(false);
      Swal.fire({
        icon: "success",
        title:
          existingBonus?.id && isEditing ? "Bonus updated" : "Bonus created",
        text: "Bonus plan saved successfully.",
        timer: 1400,
        showConfirmButton: false,
      });
      if (typeof onSaved === "function") onSaved();
    } catch (error) {
      Swal.fire("Error", error?.message || "Failed to save bonus.", "error");
    } finally {
      setSaving(false);
    }
  };

  const showForm =
    hasBonusEnabled && (!existingBonus || isEditing);

  return (
    <section
      className={`flex h-full flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition ${
        isEditing ? "border-amber-300" : "border-slate-200"
      }`}
    >
      <div className="flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-[#FFFBF5] to-white px-3 py-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-500 text-white">
          <FaGift size={11} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="mb-0 text-[13px] font-semibold text-slate-900">
              Bonus plan
            </h3>
            {!savedPlan ? (
              <Chip tone="amber">Setup</Chip>
            ) : !hasBonusEnabled ? (
              <Chip tone="slate">No bonus</Chip>
            ) : existingBonus ? (
              <Chip tone={isEditing ? "amber" : "green"}>
                {isEditing ? "Editing" : existingBonus.status || "active"}
              </Chip>
            ) : (
              <Chip tone="amber">Setup</Chip>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <BonusYesNoToggle
            value={hasBonusEnabled}
            disabled={togglingBonus || saving || planLoading}
            onChange={handleBonusToggle}
          />
          {hasBonusEnabled && existingBonus && !isEditing && savedPlan?.id ? (
            <>
              <button
                type="button"
                onClick={() => {
                  resetFormFromExisting(existingBonus);
                  setDeductFromCommissionable(bonusFromCommissionable);
                  setIsEditing(true);
                }}
                className="inline-flex h-7 items-center gap-1 rounded-md bg-amber-600 px-2 text-[10px] font-semibold text-white hover:bg-amber-700"
                style={{ border: "none" }}
              >
                <FaEdit size={9} />
                Edit
              </button>
              <button
                type="button"
                onClick={async () => {
                  const result = await Swal.fire({
                    icon: "warning",
                    title: "Are you sure?",
                    text: "Are you sure you want to delete this bonus?",
                    showCancelButton: true,
                    confirmButtonText: "Yes, delete it",
                    cancelButtonText: "Cancel",
                    confirmButtonColor: "#dc2626",
                    cancelButtonColor: "#64748b",
                    reverseButtons: true,
                  });

                  if (!result.isConfirmed) return;

                  try {
                    await deleteNewCommissionBonus(existingBonus.id);

                    await Swal.fire({
                      icon: "success",
                      title: "Bonus deleted",
                      text: "Bonus deleted successfully.",
                      confirmButtonColor: "#007BFF",
                    });

                    if (onSaved) {
                      await onSaved();
                    }
                  } catch (error) {
                    await Swal.fire({
                      icon: "error",
                      title: "Delete failed",
                      text:
                        error?.response?.data?.message ||
                        error?.message ||
                        "Failed to delete bonus.",
                      confirmButtonColor: "#007BFF",
                    });
                  }
                }}
                className="inline-flex h-7 items-center gap-1 rounded-md bg-red-600 px-2 text-[10px] font-semibold text-white hover:bg-red-700"
                style={{ border: "none" }}
              >
                Delete
              </button>
            </>
          ) : null}
          {hasBonusEnabled && showForm ? (
            <>
              <button
                type="button"
                onClick={() => {
                  resetFormFromExisting(existingBonus);
                  setDeductFromCommissionable(bonusFromCommissionable);
                  if (existingBonus) setIsEditing(false);
                }}
                disabled={saving}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
              >
                <FaTimes size={9} />
                {existingBonus ? "Cancel" : "Reset"}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSubmit}
                className={`inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-[11px] font-semibold text-white ${
                  canSubmit
                    ? "bg-amber-600 hover:bg-amber-700"
                    : "cursor-not-allowed bg-amber-400/50"
                }`}
                style={{ border: "none" }}
              >
                <FaCheck size={9} />
                {saving ? "Saving…" : existingBonus && isEditing ? "Save" : "Create"}
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-2.5 p-2.5">
        {planLoading && !existingBonus && !savedPlan ? (
          <div className="flex items-center justify-center py-6 text-[12px] text-slate-400">
            Loading…
          </div>
        ) : !hasBonusEnabled ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
            Bonus is set to <strong>No</strong>.
            Switch to <strong>Yes</strong> to add and manage a bonus.
          </div>
        ) : (
          <>
            {!showForm ? (
              <BonusSavedView
                bonus={existingBonus}
                currency={currency}
                commissionableTuitionFee={tuitionFee}
                deductFromCommissionable={bonusFromCommissionable}
              />
            ) : (
              <div className="flex flex-col gap-2.5">
            <div className="flex flex-wrap gap-1.5">
              <Chip tone="amber">{location}</Chip>
              {suggestedBonus > 0 ? (
                <Chip>Suggested {formatPercent(suggestedBonus, 2)}</Chip>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <label className="col-span-2 block min-w-0">
                <FieldLabel>Commissionable fee</FieldLabel>
                <select
                  value={deductFromCommissionable ? "deduct" : "separate"}
                  disabled={saving || planLoading}
                  onChange={(e) =>
                    setDeductFromCommissionable(e.target.value === "deduct")
                  }
                  className={controlClass}
                >
                  <option value="separate">Bonus is separate</option>
                  <option value="deduct">Deduct bonus from commissionable</option>
                </select>
              </label>
              <label className="col-span-2 block min-w-0">
                <FieldLabel>Bonus name</FieldLabel>
                <input
                  type="text"
                  value={bonusName}
                  disabled={saving}
                  onChange={(e) => setBonusName(e.target.value)}
                  className={controlClass}
                  placeholder="e.g. Provider bonus"
                />
              </label>
              {/* <label className="block min-w-0">
                <FieldLabel>Type</FieldLabel>
                <select
                  value={bonusType}
                  disabled={saving}
                  onChange={(e) => setBonusType(e.target.value)}
                  className={controlClass}
                >
                  <option value="fixed">Fixed amount</option>
                  <option value="percentage">Percentage</option>
                </select>
              </label> */}
              <div className="grid grid-cols-2 gap-3">
                <label className="block min-w-0">
                  <FieldLabel
                    hint={
                      bonusType === "percentage" ? "% of tuition" : currency
                    }
                  >
                    Bonus amount
                  </FieldLabel>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={bonusValue}
                    disabled={saving}
                    onChange={(e) => setBonusValue(e.target.value)}
                    className={controlClass}
                    placeholder="Enter bonus amount"
                  />
                </label>

                <label className="block min-w-0">
                  <FieldLabel>Tax</FieldLabel>

                  <select
                    value={taxType}
                    disabled={saving}
                    onChange={(e) => setTaxType(e.target.value)}
                    className={controlClass}
                  >
                    <option value="inclusive">Inclusive</option>
                    <option value="exclusive">Exclusive</option>
                    <option value="notax">No Tax</option>
                  </select>

                  <p className="mb-0 mt-1.5 text-[11px] text-slate-400">
                    {taxFormulaText(taxType)}
                  </p>
                </label>
              </div>
              <label className="col-span-2 block min-w-0">
                <FieldLabel hint="Optional">Remarks</FieldLabel>
                <input
                  type="text"
                  value={remarks}
                  disabled={saving}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Optional remarks…"
                  className={controlClass}
                />
              </label>
            </div>

            <div className="rounded-xl border border-amber-200/70 bg-[#FFFBF5] p-3">
              <div className="mb-3 flex items-end justify-between gap-3">
                <div>
                  <p className="mb-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Expected
                  </p>
                  <p className="mb-0 text-[22px] font-bold tabular-nums text-amber-700">
                    {formatCurrency(preview.expectedBonus, currency)}
                  </p>
                </div>
                <div className="text-right text-[10px] text-slate-400">
                  <p className="mb-0">
                    Base {formatCurrency(preview.baseBonus, currency)}
                  </p>
                  <p className="mb-0">
                    GST {formatCurrency(preview.gstAmount, currency)}
                  </p>
                </div>
              </div>
            </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default CommissionDetailBonusCard;
